import {ChangeDetectionStrategy, Component, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router, UrlTree} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from "../_services/auth.service";
import {CommunitiesService} from "../_services/communities.service";
import {Community} from "../_types/communities";
import {LocalStorageService} from "../_services/local-storage.service";
import {mostReadable, TinyColor} from "@ctrl/tinycolor";
import {AuthUser, SSOProvider, TwoFaResponse} from "../_types/auth";
import {MatDialog} from "@angular/material/dialog";
import {SsoModalComponent} from "./sso-modal/sso-modal.component";

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent {
    loginForm: FormGroup;
    requestPasswordForm: FormGroup;
    otpForm: FormGroup;
    loading: boolean = false;
    initialLoad: boolean = true;
    passwordType: string = 'password';
    view: 'Login' | 'Create' | 'Forgot' | 'Code' = 'Login'
    companyImg: string | null = null;
    companyName: string = localStorage.getItem('communityName') || 'Explain'
    community: Community | undefined;
    returnUrl: string | undefined;
    loginData: AuthUser | undefined;
    sentTo: TwoFaResponse | undefined;
    timer: number = 0;
    enteringVerificationEmail: boolean = false;
    SSOProviders: SSOProvider[] = []

    constructor(private readonly formBuilder: FormBuilder,
                private readonly communitiesService: CommunitiesService,
                private authService: AuthService,
                private readonly router: Router,
                private readonly snackBar: MatSnackBar,
                private readonly localStorageService: LocalStorageService,
                public dialog: MatDialog,
                private readonly route: ActivatedRoute) {
        this.dialog.closeAll();
        this.communitiesService.getSpecificCommunity(this.authService.pendingSSOEmailVerification?.community_id).then((r): void => {
            this.initialLoad = false;
            if (r.login_page_custom_logo) {
                this.companyImg = r.login_page_custom_logo;
            }
            this.companyName = r.name;
            this.community = r;
            if (this.community?.login_card_text_color_hex) {
                document.documentElement.style.setProperty(
                    `--login-text`, this.community?.login_card_text_color_hex
                );
            } else if (this.community?.login_card_background_color_hex) {
                document.documentElement.style.setProperty(
                    `--login-text`, this.getContrastColor(this.community.login_card_background_color_hex)
                );
            } else {
                document.documentElement.style.setProperty(
                    `--login-text`, 'black'
                );
            }
            this.localStorageService.setItem(r, 'lastSelectedCommunity')
            this.communitiesService.setCurrentDashboardCommunity(r);
            this.authService.getCommunitySSOProviders(r.id).subscribe({
                next: (res): void => {
                    for (let provider of res.data.providers) {
                        if (provider.enabled) {
                            this.SSOProviders.push(provider);
                        }
                    }
                },
                error: (e): void => {
                    console.error(e)
                }
            });
        }).catch((err): void => {
            this.openSnackBar('No community found on this domain', 'error')
            this.initialLoad = false;
            this.communitiesService.setCurrentDashboardCommunity(null);
        });

        this.loginForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
            keep_logged_in: [false]
        });

        this.requestPasswordForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
        });
        this.otpForm = this.formBuilder.group({
            code: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.email]],
        });

        let lastSelectedCommunity: Community | null = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
        const pendingMfa = this.authService.pendingMfaUser;
        const pendingSSO = this.authService.pendingSSOEmailVerification;
        if (!pendingMfa && !pendingSSO) localStorage.clear();
        if (lastSelectedCommunity) {
            localStorage.setItem('lastSelectedCommunity', JSON.stringify(lastSelectedCommunity))
        }
        if (pendingMfa) {
            this.loginData = pendingMfa;
            this.request2fa();
        }
        if (pendingSSO) {
            this.view = 'Code';
            this.sentTo = {email: pendingSSO.email, sent: pendingSSO.sent};
            this.enteringVerificationEmail = !pendingSSO.sent;
            this.otpForm.controls['email'].setValue(pendingSSO.email);
        }
        this.route.queryParams.subscribe(params => {
            const pending = this.authService.pendingMfaUser;
            if (pending && this.loginData !== pending) {
                this.loginData = pending;
                this.request2fa();
            }
            if (params['returnUrl']) {
                this.validateReturnUrl(decodeURIComponent(params['returnUrl']))
            }
            if (!params['register'] && this.view == 'Create') {
                this.view = 'Login';
            } else if (params['force_register']) {
                this.view = 'Create';
            }
        });

    }

    @HostListener('document:keydown.enter', ['$event'])
    onEnterPress(event: KeyboardEvent): void {
        if (this.view === 'Login') {
            this.loginIfValid()
        }
    }

    validateReturnUrl(url: string): void {
        const validPathRegex = /^[a-zA-Z0-9\-._~\/?&=]*$/;
        // Check if the URL matches the valid path pattern
        if (url != undefined && url && validPathRegex.test(url) && !url.includes('404') && !url.includes('passthrough')) {
            try {
                const urlTree: UrlTree = this.router.parseUrl(url);
                // If parseUrl doesn't throw an error, the URL is considered valid
                this.returnUrl = url;
            } catch (e) {
                console.warn('Invalid URL:', url);
                // Handle invalid URL scenario
            }
        }
    }

    showPassword(): void {
        if (this.passwordType == 'password') {
            this.passwordType = 'text';
        } else {
            this.passwordType = 'password';
        }
    }

    login(): void {
        this.loading = true;
        let body = this.loginForm.value;
        body.community_id = this.community?.id
        let referrer: string | null = this.route.snapshot.queryParamMap.get('referrer')
        if (referrer) {
            body.referrer = referrer;
        }
        this.authService.login(body)
            .subscribe(
                (data: any): void => {
                    this.localStorageService.removeItem('isApp')
                    if (data.data.mfa_required) {
                        this.loginData = data.data;
                        this.request2fa();
                        this.loading = false;
                    } else {
                        this.setUserAndCommunity(data.data)
                    }
                },
                (error: any): void => {
                    this.openSnackBar(error, 'error')
                    this.loading = false;
                });
    }

    finishLogin(): void {
        const challenge = this.authService.pendingSSOEmailVerification;
        if (challenge) {
            this.loading = true;
            this.authService.verifySSOEmail(challenge.token, this.otpForm.value.code).subscribe({
                next: response => {
                    this.authService.completedSSOEmailLogin = response.data;
                    this.router.navigate(['/passthrough']);
                },
                error: error => {
                    this.loading = false;
                    this.openSnackBar(error, 'error');
                }
            });
            return;
        }
        if (!this.loginData) {
            this.openSnackBar('No login token returned from the server please try again', 'error')
            this.loading = false;
            return;
        }
        this.loading = true;
        this.authService.verify2fa({code: this.otpForm.value.code}, this.loginData.token)
            .subscribe({
                next: data => {
                    if (data.verified && this.loginData) {
                        this.setUserAndCommunity(this.loginData);
                    } else {
                        this.loading = false;
                        this.openSnackBar('No login data', 'error')
                    }
                }, error: error => {
                    this.loading = false;
                    this.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
    }

    setUserAndCommunity(data: AuthUser): void {
        if (data.user.is_superuser) {
            this.loginRedirect(data)
        } else {
            this.communitiesService.getUserCommunities(data.token).subscribe(res => {
                const i: number = res.findIndex((x: any): boolean => x.community.id === this.community?.id);
                if (i != null && i >= 0) {
                    this.communitiesService.setCurrentDashboardCommunity(res[i].community)
                    this.loginRedirect(data)

                } else if (window.location.hostname === 'app.explainonline.co.uk') {
                    this.loading = false;
                    this.communitiesService.setCurrentDashboardCommunity(res[0].community);
                    this.authService.setUser(data);
                    // Could redirect users to their subdomain?
                    // window.location.href = res[0].community.front_end_url + 'passthrough?token=' + data.data.token, true;
                    if (this.community?.home_page_path) {
                        this.router.navigateByUrl(this.community.home_page_path);
                    } else {
                        this.router.navigate(['/dashboard']);
                    }
                } else {
                    this.loading = false;
                    this.openSnackBar('Sorry you are not a member of ' + this.community?.name + ' please register', 'error')
                }
            })
        }
    }

    loginRedirect(data: any): void {
        // The user has been logged in
        if (this.community?.home_page_path) {
            this.localStorageService.setItem(this.community?.home_page_path, 'homePath')
        } else {
            this.localStorageService.removeItem('homePath')
        }
        let url: string = window.location.origin;
        url = url.toLowerCase()
        if (this.community && (url.includes(this.community.alternative_front_end_url?.toLowerCase()) ||
            this.community.alternative_front_end_url?.toLowerCase().includes(url) ||
            this.community.alternative_front_end_url === url) && !url.includes('localhost')) {
            // if this is the alternative frontend url redirect to the frontend url with the passthrough and returnurl
            let redirectURL: string = this.community.front_end_url
            if (!this.community.front_end_url.endsWith("/")) {
                redirectURL = redirectURL + '/'
            }
            redirectURL = redirectURL + 'passthrough?token=' + data.token
            if (this.returnUrl) {
                redirectURL = redirectURL + '&returnUrl=' + encodeURIComponent(this.returnUrl)
            }
            window.location.href = redirectURL, true;
        } else {
            this.authService.setUser(data);
            // Feed has other params need to check if just useless redirects caused by guards.
            if (this.returnUrl && (this.returnUrl !== '/dashboard' && this.returnUrl !== '/feed' && this.returnUrl !== '/passthrough')) {
                this.router.navigateByUrl(this.returnUrl);
            } else {
                if (this.community?.home_page_path) {
                    this.router.navigateByUrl(this.community.home_page_path);
                } else {
                    this.router.navigate(['/dashboard']);
                }
            }
        }
    }

    passwordResetRequest(): void {
        this.loading = true;
        this.authService.requestPasswordReset(this.requestPasswordForm.value.email, this.community?.id)
            .subscribe(
                (data: any): void => {
                    this.loading = false;
                    this.openSnackBar(data.message, 'success')
                    this.view = 'Login';
                },
                (error: any): void => {
                    this.openSnackBar(error, 'error')
                    this.loading = false;
                });
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    goToRegister() {
        this.view = 'Create'
        this.router.navigate([], {
            queryParams: {'register': true},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    getContrastColor(color?: string): string | null {
        if (color) {
            let fallback: string = new TinyColor(color).isLight() ? 'rgba(black, 0.87)' : 'white';
            let mostReadableColor = mostReadable(color, ["#ffffff", "#000000", "#808080", "#C0C0C0"], {includeFallbackColors: true})?.toHexString()
            if (!mostReadableColor) {
                mostReadableColor = fallback;
            }
            return mostReadableColor
        } else {
            return null
        }
    }

    loginIfValid(): void {
        if (!this.loginForm.invalid && !this.loading) {
            this.login();
        }
    }

    finishLoginIfValid(invalid: boolean): void {
        if (!invalid) {
            if (this.enteringVerificationEmail) this.request2fa();
            else this.finishLogin();
        }
    }

    /** Validates whichever input is currently displayed by the shared verification form. */
    get verificationInvalid(): boolean {
        return this.otpForm.controls[this.enteringVerificationEmail ? 'email' : 'code'].invalid;
    }

    request2fa(): void {
        const challenge = this.authService.pendingSSOEmailVerification;
        if (!challenge && !this.loginData?.token) {
            this.openSnackBar('No login token returned from the server please try again', 'error')
            this.loading = false;
            return;
        }
        const request = challenge
            ? this.authService.requestSSOEmailCode(challenge.token, this.otpForm.value.email)
            : this.authService.request2fa(this.loginData!.token);
        request.subscribe({
            next: r => {
                this.enteringVerificationEmail = false;
                this.view = 'Code'
                this.sentTo = r;
                this.timer = 25;
                let timeleft: number = 25;
                let that: this = this;
                const downloadTimer: ReturnType<typeof setTimeout> = setInterval(function (): void {
                    if (timeleft <= 0) {
                        clearInterval(downloadTimer);
                    }
                    that.timer = timeleft;
                    timeleft -= 1;
                }, 1000);
            }, error: error => {
                this.openSnackBar(error, 'error')
                console.error(error)
                this.loading = false;
            }
        });
    }

    ssoModal(): void {
        this.dialog.open(SsoModalComponent, {
            autoFocus: false,
            minWidth: '0',
            width: '348px',
            data: {providers: this.SSOProviders, community: this.community?.id},
        });
    }
}
